/**
 * Helper de telemetría client-side (F3 Tel-3, ADR 0005).
 *
 * Única función pública: `trackSectionView(sectionKey)`.
 *
 * Contrato de comportamiento:
 *  - Fire-and-forget: no await, errores de red ignorados silenciosamente.
 *    La telemetría NO debe romper ni bloquear la navegación BI.
 *  - Debounce 2s: si el user cambia de sección rápido, solo se emite
 *    el último en la ventana de 2s.
 *  - Dedupe: si el sectionKey es IGUAL al último emitido, se cancela
 *    (el ADR 0005 pide evitar spam de la misma sección).
 *  - 204 silencioso: el worker devuelve 204 para platform staff sin org
 *    activa → no hay nada que reportar; ignoramos sin log.
 *  - Cualquier otro error de red → ignorado (fail-open de telemetría).
 *
 * Implementación:
 *  - Un único timer pendiente (`pendingTimer`) + último sectionKey enviado
 *    (`lastEmitted`) como estado de módulo (singleton). Suficiente para
 *    el caso de uso (una instancia de App por tab del browser).
 *  - Si el backend rechaza (403, 422, 429), la UI no ve el error — la
 *    función ya es fire-and-forget.
 */

const DEBOUNCE_MS = 2000;

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let lastEmitted: string | null = null;

/**
 * Emite `section_viewed` al worker de forma asíncrona (fire-and-forget).
 *
 * @param sectionKey - Clave de la sección visible (ej. "resumen", "er").
 */
export function trackSectionView(sectionKey: string): void {
  // Cancelar timer pendiente si hay uno (debounce: reiniciar ventana).
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }

  pendingTimer = setTimeout(() => {
    pendingTimer = null;

    // Dedupe: no emitir si el sectionKey es el mismo que el último emitido.
    if (sectionKey === lastEmitted) return;

    lastEmitted = sectionKey;

    const apiUrl = import.meta.env.VITE_API_URL ?? "http://localhost:6787";

    // Fire-and-forget: no await, errores ignorados silenciosamente.
    fetch(`${apiUrl}/api/usage/events`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "portal-dashboard",
      },
      body: JSON.stringify({ eventType: "section_viewed", sectionKey }),
    }).catch(() => {
      // Error de red: ignorado. La telemetría no rompe la navegación.
      // 204 (platform staff sin org) también pasa por aquí y queda ignorado.
    });
  }, DEBOUNCE_MS);
}

/**
 * Resetea el estado interno del tracker.
 * Solo para tests — no llamar desde código de producción.
 */
export function _resetTrackerForTests(): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  lastEmitted = null;
}
